import { DEFAULT_FRAME, FrameDef, frameOf } from './frames'
import { FrameId } from './pipeline-types'
import { Knob, Metric, ViewConfig } from './view-config'

/**
 * Build-your-own report: pure config data + transforms for the builder page.
 * The builder only ever edits a ViewConfig — same contract as templates and
 * saved views — so preview, export and save-as all come for free.
 *
 * Everything that used to be deal-specific (the measures, group-by fields,
 * default columns, filter knobs) now derives from the config's frame, so the
 * same builder drives any frame.
 */

/** A frame's default measure (first in its list) — the safe fallback. */
const firstMeasure = (frame: FrameDef): Metric => frame.measures[0]

/** Sensible default sort field for a list: the trend date, else first column. */
const defaultSortField = (frame: FrameDef): string =>
  frame.trendDate ?? frame.columns[0]

/** Categorical multi-select filter knobs (first few cat dims of the frame). */
const filterKnobs = (frame: FrameDef): Knob[] =>
  frame.catDims.slice(0, 3).map((field) => ({
    type: 'filter-select',
    field,
    label: frame.labels[field] ?? field,
  }))

const tableKnobs = (frame: FrameDef): Knob[] => [
  ...filterKnobs(frame),
  { type: 'column-picker' },
  { type: 'limit', presets: [25, 100, 0] },
]

/**
 * Starting point for the flat builder: the raw frame — all columns, all rows,
 * no operations yet. `columns: undefined` means "all columns" (see ReportTable);
 * no sort/limit means natural order, every row.
 */
export const blankFrame = (frame: FrameId = DEFAULT_FRAME): ViewConfig => ({
  id: 'custom',
  name: 'Untitled report',
  description: '',
  frame,
  kind: 'list',
  filters: [],
  columns: undefined,
  sort: undefined,
  limit: undefined,
  chart: 'table-only',
  knobs: [],
})

/** Starting point: a filtered table seeded with the frame's default columns. */
export const blankReport = (frame: FrameId = DEFAULT_FRAME): ViewConfig => {
  const f = frameOf(frame)
  return {
    id: 'custom',
    name: 'Untitled report',
    description: '',
    frame,
    kind: 'list',
    filters: [],
    columns: f.defaultColumns,
    sort: { by: defaultSortField(f), dir: 'desc' },
    limit: 10,
    chart: 'table-only',
    knobs: tableKnobs(f),
  }
}

/** Flip a builder config to table kind, keeping filters. */
export const toTableKind = (c: ViewConfig): ViewConfig => {
  const f = frameOf(c.frame)
  return {
    ...c,
    kind: 'list',
    chart: 'table-only',
    groupBy: undefined,
    seriesBy: undefined,
    metrics: undefined,
    chartMetric: undefined,
    cumulative: undefined,
    // A row list has no aggregated measure, so a target/KPI can't grade it —
    // drop any threshold rather than carry a stale one across the shape change.
    threshold: undefined,
    columns: c.columns ?? f.defaultColumns,
    sort: { by: defaultSortField(f), dir: 'desc' },
    limit: c.limit ?? 10,
    knobs: tableKnobs(f),
  }
}

/**
 * Flip a builder config to a metric: an aggregate with NO groupBy that resolves
 * to a single rollup value. Keeps filters; drops grouping/series/columns.
 */
export const toMetricKind = (c: ViewConfig): ViewConfig => {
  const f = frameOf(c.frame)
  return {
    ...c,
    kind: 'aggregate',
    chart: 'table-only',
    groupBy: undefined,
    seriesBy: undefined,
    metrics: c.metrics ?? [firstMeasure(f)],
    chartMetric: c.chartMetric ?? firstMeasure(f).label,
    // No group-by → no chronological axis → running total can't apply.
    cumulative: undefined,
    columns: undefined,
    sort: undefined,
    limit: undefined,
    knobs: filterKnobs(f),
  }
}

/** Flip a builder config to chart kind, keeping filters. */
export const toChartKind = (c: ViewConfig): ViewConfig => {
  const f = frameOf(c.frame)
  return {
    ...c,
    kind: 'aggregate',
    chart: 'bar',
    groupBy: c.groupBy ?? { field: f.catDims[0] },
    // Keep a running total only if the (preserved) breakdown is a time bucket.
    cumulative: c.groupBy?.bucket ? c.cumulative : undefined,
    metrics: c.metrics ?? [firstMeasure(f)],
    chartMetric: c.chartMetric ?? firstMeasure(f).label,
    columns: undefined,
    sort: undefined,
    limit: undefined,
    knobs: filterKnobs(f),
  }
}

/**
 * Replace the metric set from selected labels, in canonical (frame) order. An
 * empty selection falls back to the frame's first measure (an aggregate without
 * metrics is meaningless). chartMetric is kept when still selected, else reset.
 */
export const setMetrics = (c: ViewConfig, labels: string[]): ViewConfig => {
  const f = frameOf(c.frame)
  const set = new Set(labels)
  const metrics = f.measures.filter((m) => set.has(m.label))
  const next = metrics.length > 0 ? metrics : [firstMeasure(f)]
  const chartMetric = next.some((m) => m.label === c.chartMetric)
    ? c.chartMetric
    : next[0].label
  return { ...c, metrics: next, chartMetric }
}

/**
 * Set the metric list directly from Metric objects, recomputing `chartMetric` to
 * the first. Unlike `setMetrics` (which looks labels up in the frame's named
 * measures), this accepts synthesized metrics — an explicit agg+field pair the
 * frame doesn't name, e.g. "Avg Monthly Volume" — so the agg/field controls can
 * build any combination. Empty falls back to the frame's first measure.
 */
export const setMetricObjects = (
  c: ViewConfig,
  metrics: Metric[]
): ViewConfig => {
  const next = metrics.length > 0 ? metrics : [firstMeasure(frameOf(c.frame))]
  return { ...c, metrics: next, chartMetric: next[0].label }
}

export const setGroupBy = (c: ViewConfig, field: string): ViewConfig => ({
  ...c,
  groupBy: { field },
})
